/**
 * Synchronous, allocation-free event bus. ARCHITECTURE.md §6.
 *
 * Payload ownership: `emit` passes a POOLED object owned by the emitter. Listeners may read
 * it during dispatch but must not retain a reference. Copy what you need.
 */

const MAX_HISTORY = 512;
const MAX_DEFERRED = 256;

export class EventBus {
  constructor({ recordHistory = false } = {}) {
    /** @type {Map<string, Array<Function>>} */
    this._listeners = new Map();
    /** Listeners registered for every event, in registration order. */
    this._wildcard = [];
    this._depth = 0;
    /** Deferred queue: parallel arrays to avoid per-emit allocation. */
    this._defNames = new Array(MAX_DEFERRED).fill('');
    this._defPayloads = new Array(MAX_DEFERRED).fill(null);
    this._defCount = 0;
    this._deferredDropped = 0;

    // Dev/test history: bounded ring of preallocated slots, shallow-copied into.
    this.recordHistory = recordHistory;
    this.history = new Array(MAX_HISTORY);
    for (let i = 0; i < MAX_HISTORY; i++) this.history[i] = { tick: 0, name: '', data: {} };
    this._historyHead = 0;
    this._historyCount = 0;

    this.emitCount = 0;
    /** @type {Map<string, number>} */
    this.emitCounts = new Map();
  }

  /**
   * @param {string} name event name, or '*' for all events
   * @returns {() => void} unsubscribe
   */
  on(name, fn) {
    if (typeof fn !== 'function') throw new TypeError(`EventBus.on(${name}): handler is not a function`);
    if (name === '*') {
      this._wildcard.push(fn);
      return () => this.off(name, fn);
    }
    let arr = this._listeners.get(name);
    if (!arr) {
      arr = [];
      this._listeners.set(name, arr);
    }
    arr.push(fn);
    return () => this.off(name, fn);
  }

  /** Subscribes for a single dispatch. */
  once(name, fn) {
    const off = this.on(name, (payload) => {
      off();
      fn(payload);
    });
    return off;
  }

  off(name, fn) {
    const arr = name === '*' ? this._wildcard : this._listeners.get(name);
    if (!arr) return;
    const i = arr.indexOf(fn);
    if (i >= 0) arr.splice(i, 1);
  }

  /** Removes every listener for a name, or all listeners when name is omitted. */
  clear(name) {
    if (name === undefined) {
      this._listeners.clear();
      this._wildcard.length = 0;
      return;
    }
    if (name === '*') {
      this._wildcard.length = 0;
      return;
    }
    this._listeners.delete(name);
  }

  listenerCount(name) {
    const arr = name === '*' ? this._wildcard : this._listeners.get(name);
    return arr ? arr.length : 0;
  }

  /**
   * Dispatches immediately. Listener exceptions are contained so one broken subscriber
   * cannot halt the simulation, but they are rethrown in deterministic runs where a
   * silent failure would corrupt a replay.
   */
  emit(name, payload) {
    this.emitCount++;
    this.emitCounts.set(name, (this.emitCounts.get(name) || 0) + 1);
    if (this.recordHistory) this._record(name, payload);

    this._depth++;
    const arr = this._listeners.get(name);
    if (arr !== undefined) {
      // Iterate over a stable length: handlers added during dispatch run next time.
      const n = arr.length;
      for (let i = 0; i < n; i++) {
        const fn = arr[i];
        if (fn === undefined) continue;
        if (this.strict) {
          fn(payload);
        } else {
          try {
            fn(payload);
          } catch (err) {
            this._onError(name, err);
          }
        }
      }
    }
    const wn = this._wildcard.length;
    for (let i = 0; i < wn; i++) {
      const fn = this._wildcard[i];
      if (this.strict) {
        fn(payload, name);
      } else {
        try {
          fn(payload, name);
        } catch (err) {
          this._onError(name, err);
        }
      }
    }
    this._depth--;
    return payload;
  }

  /**
   * Queues an event for dispatch at the end of the current fixed step. Use when an
   * emitter must finish mutating state before listeners observe it.
   *
   * The payload must remain valid until `flushDeferred` runs — i.e. do not reuse the
   * same pooled payload for two deferred emits in one step.
   */
  defer(name, payload) {
    if (this._defCount >= MAX_DEFERRED) {
      this._deferredDropped++;
      return;
    }
    this._defNames[this._defCount] = name;
    this._defPayloads[this._defCount] = payload;
    this._defCount++;
  }

  flushDeferred() {
    // Snapshot the count: events deferred *during* the flush run on the next flush,
    // which keeps a single step bounded.
    const n = this._defCount;
    if (n === 0) return 0;
    for (let i = 0; i < n; i++) {
      const name = this._defNames[i];
      const payload = this._defPayloads[i];
      this._defPayloads[i] = null;
      this.emit(name, payload);
    }
    if (this._defCount === n) {
      this._defCount = 0;
    } else {
      // Compact the tail that was appended during dispatch.
      const extra = this._defCount - n;
      for (let i = 0; i < extra; i++) {
        this._defNames[i] = this._defNames[n + i];
        this._defPayloads[i] = this._defPayloads[n + i];
      }
      this._defCount = extra;
    }
    return n;
  }

  get deferredCount() {
    return this._defCount;
  }

  get deferredDropped() {
    return this._deferredDropped;
  }

  _record(name, payload) {
    const slot = this.history[this._historyHead];
    slot.tick = payload && typeof payload.tick === 'number' ? payload.tick : -1;
    slot.name = name;
    // Shallow copy into the existing object: no allocation for the target.
    const data = slot.data;
    for (const k in data) delete data[k];
    if (payload) for (const k in payload) data[k] = payload[k];
    this._historyHead = (this._historyHead + 1) % MAX_HISTORY;
    if (this._historyCount < MAX_HISTORY) this._historyCount++;
  }

  /** Oldest-first array of recorded events. Dev/test only — allocates. */
  historyList() {
    const out = [];
    const start = (this._historyHead - this._historyCount + MAX_HISTORY) % MAX_HISTORY;
    for (let i = 0; i < this._historyCount; i++) {
      const slot = this.history[(start + i) % MAX_HISTORY];
      out.push({ tick: slot.tick, name: slot.name, data: { ...slot.data } });
    }
    return out;
  }

  resetHistory() {
    this._historyHead = 0;
    this._historyCount = 0;
    this.emitCount = 0;
    this.emitCounts.clear();
  }

  _onError(name, err) {
    // Logger is optional so the bus stays dependency-free.
    if (this.onError) this.onError(name, err);
    else console.error(`[events] handler for "${name}" threw:`, err);
  }
}
